import api from "./api";

/* ============================================================
 * Expense module API client.
 * Mirrors the leave api: a thin wrapper over the shared `api`
 * axios instance (auth token attached by interceptor).
 * Backend router prefix: /expenses
 * ========================================================== */

export interface ExpenseLineItem {
  id?: string;
  category: string;
  amount: number;
  spend_date: string | null;
  description?: string | null;
  receipt_url?: string | null;
  receipt_filename?: string | null;
  // GST breakdown. On create, the client sends the bases (taxable_amount,
  // tax_rate, cess_percent); the server recomputes cgst/sgst/igst/cess_amount
  // and the line total. On read, all are populated (0 on tax-off reports).
  taxable_amount?: number;
  tax_rate?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  cess_percent?: number;
  cess_amount?: number;
}

export type ExpenseStatus =
  | "PENDING"
  | "APPROVED"
  | "REIMBURSED"
  | "PAID"
  | "REJECTED"
  | "WITHDRAWN";

export interface ExpenseReport {
  id: string;
  title: string;
  employeeID: string | null;
  name: string | null;
  note: string | null;
  total_amount: number;
  currency: string;
  supplierName: string | null;
  supplierInvoiceNo: string | null;
  supplierInvoiceDate: string | null;
  expenseVoucherDate: string | null;
  vendorID: string | null;
  clientID: string | null;
  vendor_state: string | null;
  tax_details: boolean;
  rcm: boolean;
  cess: boolean;
  already_paid: boolean;
  approverID: string | null;
  status: ExpenseStatus;
  submitted_on: string | null;
  decision_by: string | null;
  decision_on: string | null;
  decision_note: string | null;
  reimbursed_by: string | null;
  reimbursed_on: string | null;
  sk_push_status: string | null;
  sk_push_error: string | null;
  sk_pushed_on: string | null;
  sk_expense_id: string | null;
  emptID: string | null;
  items: ExpenseLineItem[];
}

export type ExpenseScope = "mine" | "pending" | "all";

/* ---------------- LIST ---------------- */
export const getExpenses = async (
  scope: ExpenseScope = "mine",
  status?: string
) => {
  const params = new URLSearchParams({ scope });
  if (status) params.set("status", status);
  const res = await api.get(`/expenses?${params.toString()}`);
  return { expenses: (res.data?.expenses || []) as ExpenseReport[] };
};

export const getExpense = async (id: string) => {
  const res = await api.get(`/expenses/${id}`);
  return res.data as ExpenseReport;
};

export const getExpenseCategories = async (): Promise<string[]> => {
  const res = await api.get(`/expenses/categories`);
  return res.data?.categories || [];
};

/* ---------------- ADD CUSTOM NATURE OF EXPENSE ---------------- */
// Adds an org-wide "Nature of Expense". Returns the refreshed merged list
// (defaults + custom). Idempotent server-side on duplicate names.
export const addExpenseCategory = async (name: string): Promise<string[]> => {
  const res = await api.post(`/expenses/categories`, { name });
  return res.data?.categories || [];
};

/* ---------------- CREATE / UPDATE ---------------- */
export interface ExpensePayload {
  title: string;
  note?: string;
  supplierName?: string;
  supplierInvoiceNo?: string;
  supplierInvoiceDate?: string | null;
  expenseVoucherDate?: string | null;
  vendorID?: string | null;
  clientID?: string | null;
  tax_details?: boolean;
  rcm?: boolean;
  cess?: boolean;
  already_paid?: boolean;
  items: ExpenseLineItem[];
}

export const createExpense = async (payload: ExpensePayload) => {
  const res = await api.post("/expenses", payload);
  return res.data as ExpenseReport;
};

// Edit an existing report (owner-only, while not yet approved). The backend
// recomputes totals/tax and resubmits the report to PENDING.
export const updateExpense = async (id: string, payload: ExpensePayload) => {
  const res = await api.put(`/expenses/${id}`, payload);
  return res.data as ExpenseReport;
};

// Soft-delete an own report while it's still editable.
export const deleteExpense = async (id: string) => {
  const res = await api.delete(`/expenses/${id}`);
  return res.data as { ok: boolean; id: string };
};

/* ---------------- RECEIPT UPLOAD ---------------- */
export const uploadReceipt = async (file: File) => {
  const form = new FormData();
  form.append("uploaded_file", file);
  const res = await api.post("/expenses/receipt", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data as { receipt_url: string; receipt_filename: string };
};

/* ---------------- APPROVE / REJECT ---------------- */
export const updateExpenseStatus = async (
  id: string,
  status: "approved" | "rejected",
  note?: string
) => {
  const res = await api.put(
    `/expenses/${id}/status?status=${status.toUpperCase()}`,
    { note: note || null }
  );
  return res.data as ExpenseReport;
};

/* ---------------- REIMBURSE / WITHDRAW ---------------- */
export const reimburseExpense = async (id: string) => {
  const res = await api.put(`/expenses/${id}/reimburse`);
  return res.data as ExpenseReport;
};

export const withdrawExpense = async (id: string) => {
  const res = await api.put(`/expenses/${id}/withdraw`);
  return res.data as ExpenseReport;
};

/* ---------------- RETRY STARTUPKHATA PUSH (admin) ---------------- */
export const retryExpensePush = async (id: string) => {
  const res = await api.post(`/expenses/${id}/retry_push`);
  return res.data;
};

/* ---------------- NOTIFICATION BELL FEED ---------------- */
export interface ExpenseNotification {
  id: string;
  kind: string; // SUBMITTED | APPROVED | REJECTED | REIMBURSED
  source: "expense";
  report_id: string | null;
  task_id: string; // "expense:<id>"
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const getExpenseNotifications = (limit = 50) =>
  api.get<{ total: number; items: ExpenseNotification[] }>(
    `/expenses/notifications/unread?limit=${limit}`
  );

export const markExpenseNotificationRead = (id: string) =>
  api.post(`/expenses/notifications/${id}/read`);
